/**
 * GitLabClustersPicker — custom field do Scaffolder.
 *
 * Picker com os clusters Kubernetes ingressados no gitlab-agent.
 * A lista vem do endpoint backend /api/ibict/gitlab/clusters, que consulta
 * os agents REGISTRADOS (cluster_agents da API GitLab) em infra/gitlab-agent
 * — nao mais os nomes de pasta em .gitlab/agents/. Isso reflete os agents de
 * fato ingressados e expoe o status de conexao.
 *
 * Por padrao permite selecionar 1+ clusters num unico campo; templates podem
 * passar `ui:options.single: true` para renderizar uma lista de selecao unica.
 * O valor continua sendo um array de nomes para manter templates e actions
 * inalterados; a resolucao nome -> agent-id acontece no backend.
 *
 * Ingressar um cluster novo (registrar o agent em infra/gitlab-agent) faz ele
 * aparecer sozinho no proximo carregamento do form (sem deploy de Backstage,
 * sem editar templates).
 */

import { useEffect, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  TextField,
  Chip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

interface ClusterAgentDTO {
  id: number;
  name: string;
  connected: boolean;
}

// Endpoint backend que lista os agents ingressados (cluster_agents) com status
// de conexao. Servido pelo plugin `ibict` (packages/backend/.../ibictGitlabGroups.ts).
const CLUSTERS_ENDPOINT = '/api/ibict/gitlab/clusters';

const GitLabClustersPicker = (
  props: FieldExtensionComponentProps<string[]>,
) => {
  const { onChange, formData, rawErrors, required, schema, uiSchema } = props;
  const fetchApi = useApi(fetchApiRef);
  const single = Boolean((uiSchema as any)?.['ui:options']?.single);

  const [clusters, setClusters] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchApi.fetch(CLUSTERS_ENDPOINT);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { clusters: ClusterAgentDTO[] };
        // Conectados primeiro, depois por nome; o valor e' sempre o nome.
        const names = [...(body.clusters ?? [])]
          .sort(
            (a, b) =>
              Number(b.connected) - Number(a.connected) ||
              a.name.localeCompare(b.name),
          )
          .map(c => c.name);
        if (!cancelled) {
          setClusters(names);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchApi]);

  const value = Array.isArray(formData) ? formData : [];
  const singleValue = value[0] ?? null;

  return (
    <FormControl
      margin="normal"
      required={required}
      error={Boolean(rawErrors?.length) || Boolean(error)}
      fullWidth
    >
      {single ? (
        <Autocomplete<string, false, false, false>
          options={clusters}
          value={singleValue}
          onChange={(_event, val) => onChange(val ? [val] : [])}
          loading={loading}
          autoSelect
          renderInput={(params: any) => (
            <TextField
              {...params}
              label={(schema.title as string) ?? 'Cluster'}
              helperText={
                error
                  ? `Falha ao carregar clusters: ${error}`
                  : ((schema.description as string) ??
                    'Selecione um cluster Kubernetes (agent do infra/gitlab-agent)')
              }
              variant="outlined"
              required={required && value.length === 0}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      ) : (
        <Autocomplete<string, true, false, false>
          multiple
          options={clusters}
          value={value}
          onChange={(_event, val) => onChange((val as string[]) ?? [])}
          loading={loading}
          autoSelect
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={option}
                size="small"
                {...getTagProps({ index })}
                key={option}
              />
            ))
          }
          renderInput={(params: any) => (
            <TextField
              {...params}
              label={(schema.title as string) ?? 'Clusters'}
              helperText={
                error
                  ? `Falha ao carregar clusters: ${error}`
                  : ((schema.description as string) ??
                    'Selecione um ou mais clusters Kubernetes (agents do infra/gitlab-agent)')
              }
              variant="outlined"
              // HTML5 required no <input> interno do Autocomplete multiple
              // bloqueia o submit com "Preencha este campo" mesmo havendo
              // chips selecionados, porque o input de busca fica vazio. A
              // validacao do schema (minItems: 1) ja cobre obrigatoriedade.
              required={required && value.length === 0}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    </FormControl>
  );
};

export const GitLabClustersPickerField = createFormField({
  name: 'GitLabClustersPicker',
  component: GitLabClustersPicker,
});

export const gitLabClustersPickerField = FormFieldBlueprint.make({
  name: 'gitlab-clusters-picker',
  params: {
    field: async () => GitLabClustersPickerField,
  },
});
